import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Checkbox, Col, DatePicker, Empty, Form, Input, InputNumber,
  Modal, Popconfirm, Row, Select, Space, Statistic, Table, Tag, Tooltip,
  Typography, message,
} from 'antd';
import {
  CheckOutlined, DeleteOutlined, DollarOutlined, EditOutlined, PlusOutlined,
  ThunderboltOutlined, UndoOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  applyParticipantFees, createFeeItem, createInstallmentPlanItem, deleteFeeItem,
  deleteInstallment, deleteInstallmentPayment, deleteInstallmentPlanItem,
  generateInstallments, generateInstallmentsForAll, getFeeItems,
  getFinanceSummary, getInstallmentPlan, getInstallments, getOrphanedInstallments,
  getParticipantFees, getParticipants, payInstallment,
  unpayInstallment, updateFeeItem, updateInstallmentPlanItem,
} from '../../api/competitions';
import apiErrorMessage from '../../utils/apiError';
import { tahsilatZamani } from '../../utils/tahsilatZamani';

const { Text } = Typography;

const CURRENCIES = [
  { value: 'TRY', label: '₺ TRY' },
  { value: 'EUR', label: '€ EUR' },
  { value: 'USD', label: '$ USD' },
];

const SYMBOL = { TRY: '₺', EUR: '€', USD: '$' };

export const money = (amount, currency) =>
  `${Number(amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${SYMBOL[currency] || currency}`;

const StageFinanceTab = ({ stage }) => {
  const [feeItems, setFeeItems] = useState([]);
  const [planItems, setPlanItems] = useState([]);
  const [summary, setSummary] = useState({ by_currency: [], overdue: [], overdue_count: 0 });
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemModal, setItemModal] = useState({ open: false, record: null });
  const [planModal, setPlanModal] = useState({ open: false, record: null });
  const [detail, setDetail] = useState({ open: false, participant: null });
  const [bulkApplying, setBulkApplying] = useState(false);
  const [form] = Form.useForm();
  const [planForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: items }, { data: plan }, { data: sum }, { data: ps }] = await Promise.all([
        getFeeItems(stage.id),
        getInstallmentPlan(stage.id),
        getFinanceSummary(stage.id),
        getParticipants(stage.id, { per_page: 100 }),
      ]);
      setFeeItems(items.data || []);
      setPlanItems(plan.data || []);
      setSummary(sum);
      setParticipants(ps.data || []);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Ücret bilgileri yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [stage.id]);

  useEffect(() => { load(); }, [load]);

  // Plani etaptaki herkese tek seferde uygula. Atlananlar sunucudan sebebiyle
  // birlikte geliyor - "uygulandi" deyip sessizce gecmek yaniltici olurdu.
  const applyPlanToAll = async () => {
    setBulkApplying(true);
    try {
      const { data } = await generateInstallmentsForAll(stage.id);
      if (data.skipped?.length) {
        Modal.info({
          title: data.message,
          width: 560,
          content: (
            <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 12 }}>
              <Text type="secondary">Atlanan katılımcılar:</Text>
              <ul style={{ paddingInlineStart: 18, marginTop: 8 }}>
                {data.skipped.map((s) => (
                  <li key={s.participant_id} style={{ marginBottom: 6 }}>
                    <Text strong>{s.student || `#${s.participant_id}`}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>{s.reason}</Text>
                  </li>
                ))}
              </ul>
            </div>
          ),
        });
      } else {
        message.success(data.message);
      }
      load();
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBulkApplying(false);
    }
  };

  const submitItem = async () => {
    try {
      const v = await form.validateFields();
      if (itemModal.record) {
        const { data } = await updateFeeItem(itemModal.record.id, v);
        message.success('Kalem güncellendi.');
        // Fiyat snapshot'landigi icin eski katilimcilarin borcu degismedi -
        // sunucu kac kisinin etkilendigini soyluyorsa uyariyi goster.
        if (data.notice) message.warning(data.notice, 8);
      } else {
        await createFeeItem(stage.id, v);
        message.success('Ücret kalemi eklendi.');
      }
      setItemModal({ open: false, record: null });
      form.resetFields();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e));
    }
  };

  const submitPlanItem = async () => {
    try {
      const v = await planForm.validateFields();
      const payload = { ...v, due_date: v.due_date ? v.due_date.format('YYYY-MM-DD') : null };
      if (planModal.record) {
        await updateInstallmentPlanItem(planModal.record.id, payload);
        message.success('Taksit planı satırı güncellendi.');
      } else {
        await createInstallmentPlanItem(stage.id, payload);
        message.success('Taksit planı satırı eklendi.');
      }
      setPlanModal({ open: false, record: null });
      planForm.resetFields();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e));
    }
  };

  return (
    <div>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {summary.by_currency?.length ? summary.by_currency.map((c) => (
          <Col key={c.currency} xs={24} sm={12} md={8}>
            <Card size="small">
              <Statistic title={`Tahakkuk (${c.currency})`} value={money(c.accrued, c.currency)} />
              <Space split="·" style={{ marginTop: 8 }}>
                <Text type="success">Tahsil: {money(c.collected, c.currency)}</Text>
                <Text type={c.remaining > 0 ? 'warning' : 'secondary'}>Kalan: {money(c.remaining, c.currency)}</Text>
              </Space>
            </Card>
          </Col>
        )) : (
          <Col span={24}>
            <Alert type="info" showIcon message="Henüz katılımcıya ücret uygulanmamış." />
          </Col>
        )}
        {summary.overdue_count > 0 && (
          <Col xs={24} sm={12} md={8}>
            <Card size="small">
              <Statistic
                title="Gecikmiş Taksit"
                value={summary.overdue_count}
                styles={{ content: { color: '#cf1322' } }}
              />
            </Card>
          </Col>
        )}
      </Row>

      <Card
        size="small"
        title="Ücret Kalemleri"
        extra={
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              setItemModal({ open: true, record: null });
              form.setFieldsValue({ currency: 'TRY', basis: 'per_person', is_optional: false });
            }}
          >
            Kalem Ekle
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Text type="secondary">
          Aynı etapta farklı para birimlerinde birden fazla kalem olabilir:
          "Organizasyon 300 € (kişi başı)" + "Servis 4.500 ₺ (kişi başı)" +
          "Aracı kurum 2.000 ₺ (sabit)".
        </Text>
        <Table
          rowKey="id"
          size="small"
          style={{ marginTop: 12 }}
          dataSource={feeItems}
          pagination={false}
          locale={{ emptyText: <Empty description="Ücret kalemi tanımlanmamış." /> }}
          columns={[
            { title: 'Kalem', dataIndex: 'label' },
            { title: 'Tutar', dataIndex: 'amount', width: 130, render: (v, r) => money(v, r.currency) },
            {
              title: 'Hesaplama',
              dataIndex: 'basis',
              width: 130,
              render: (v) => (v === 'per_person' ? <Tag>Kişi başı</Tag> : <Tag color="blue">Sabit</Tag>),
            },
            {
              title: 'Zorunluluk',
              dataIndex: 'is_optional',
              width: 130,
              render: (v) => (v ? <Tag color="orange">İsteğe bağlı</Tag> : <Tag color="green">Zorunlu</Tag>),
            },
            {
              title: 'İşlemler',
              width: 110,
              fixed: 'right',
              render: (_, r) => (
                <Space>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => { setItemModal({ open: true, record: r }); form.setFieldsValue(r); }}
                  />
                  <Popconfirm
                    title="Kalem silinsin mi?"
                    description="Katılımcılara daha önce uygulanmış tutarlar korunur."
                    onConfirm={async () => {
                      try {
                        await deleteFeeItem(r.id);
                        message.success('Silindi.');
                        load();
                      } catch (e) {
                        message.error(apiErrorMessage(e));
                      }
                    }}
                    okText="Sil"
                    cancelText="Vazgeç"
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Card
        size="small"
        title="Taksit Planı (Etap Geneli)"
        extra={
          <Space>
            <Popconfirm
              title="Plan etaptaki herkese uygulansın mı?"
              description="Yalnızca HENÜZ TAKSİT PLANI OLMAYAN katılımcılara uygulanır; planı olanlar (tahsilat girilmiş olsun olmasın) atlanır ve hiçbir taksitleri değişmez. Ücreti girilmemiş olanlara önce zorunlu kalemler uygulanır, isteğe bağlı kalemler (servis vb.) kişi bazında kalır. Bir kişinin planını yenilemek için o katılımcının Ücret ve Taksitler ekranını açın."
              onConfirm={applyPlanToAll}
              okText="Uygula"
              cancelText="Vazgeç"
            >
              <Button size="small" icon={<ThunderboltOutlined />} loading={bulkApplying}>
                Tümüne Uygula
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setPlanModal({ open: true, record: null })}
            >
              Satır Ekle
            </Button>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Text type="secondary">
          Taksit takvimi burada bir kere tanımlanır — bu etaba katılan herkes
          aynı tarihleri ve aynı kişi başı tutarları görür. Taksitler eşit
          olmak zorunda değil (ör. 1. taksit kişi başı 200 €, sonraki 2 taksit
          50&apos;şer €). Katılımcıya uygulanırken tutar sadece o ailenin
          getirdiği kişi sayısıyla çarpılır.
        </Text>
        <Table
          rowKey="id"
          size="small"
          style={{ marginTop: 12 }}
          dataSource={planItems}
          pagination={false}
          locale={{ emptyText: <Empty description="Taksit planı tanımlanmamış." /> }}
          columns={[
            { title: 'Para Birimi', dataIndex: 'currency', width: 100 },
            { title: 'No', dataIndex: 'no', width: 60 },
            { title: 'Taksit', dataIndex: 'label' },
            { title: 'Vade', dataIndex: 'due_date', width: 120, render: (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '—') },
            {
              title: 'Kişi Başı Tutar',
              dataIndex: 'per_person_amount',
              width: 140,
              render: (v, r) => money(v, r.currency),
            },
            {
              title: 'İşlemler',
              width: 110,
              fixed: 'right',
              render: (_, r) => (
                <Space>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setPlanModal({ open: true, record: r });
                      planForm.setFieldsValue({ ...r, due_date: r.due_date ? dayjs(r.due_date) : null });
                    }}
                  />
                  <Popconfirm
                    title="Taksit planı satırı silinsin mi?"
                    onConfirm={async () => {
                      try {
                        await deleteInstallmentPlanItem(r.id);
                        message.success('Silindi.');
                        load();
                      } catch (e) {
                        message.error(apiErrorMessage(e));
                      }
                    }}
                    okText="Sil"
                    cancelText="Vazgeç"
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Card size="small" title="Katılımcı Bazında Ücret ve Tahsilat">
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={participants}
          scroll={{ x: 700 }}
          pagination={{ pageSize: 20, showTotal: (t) => `${t} katılımcı` }}
          locale={{ emptyText: <Empty description="Katılımcı yok." /> }}
          columns={[
            {
              title: 'Öğrenci',
              render: (_, r) => `${r.student?.name || ''} ${r.student?.surname || ''}`.trim() || '—',
            },
            { title: 'Kişi', dataIndex: 'attendee_count', width: 70 },
            {
              title: 'Ücret Onayı',
              dataIndex: 'fee_consent_status',
              width: 120,
              render: (v) => (
                <Tag color={v === 'approved' ? 'success' : v === 'declined' ? 'error' : 'default'}>
                  {v === 'approved' ? 'Onayladı' : v === 'declined' ? 'Vazgeçti' : 'Bekliyor'}
                </Tag>
              ),
            },
            {
              title: 'İşlemler',
              width: 150,
              fixed: 'right',
              render: (_, r) => (
                <Button size="small" icon={<DollarOutlined />} onClick={() => setDetail({ open: true, participant: r })}>
                  Ücret / Taksit
                </Button>
              ),
            },
          ]}
        />
      </Card>

      {summary.overdue?.length > 0 && (
        <Card size="small" title="Gecikmiş Taksitler" style={{ marginTop: 16 }}>
          <Table
            rowKey="id"
            size="small"
            dataSource={summary.overdue}
            pagination={false}
            columns={[
              {
                title: 'Öğrenci',
                render: (_, r) => `${r.participant?.student?.name || ''} ${r.participant?.student?.surname || ''}`.trim() || '—',
              },
              { title: 'Taksit', dataIndex: 'label' },
              { title: 'Vade', dataIndex: 'due_date', width: 120, render: (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '—') },
              {
                // Taksitin TAM tutari degil KALAN borc gosteriliyor: kismi
                // tahsilat yapilmis bir taksitte tam tutar yaniltici olur,
                // "ne kadar istenecek" sorusunun cevabi kalandir.
                title: 'Kalan Miktar',
                dataIndex: 'remaining_amount',
                width: 150,
                render: (v, r) => (
                  <>
                    {money(v ?? r.amount, r.currency)}
                    {Number(r.paid_amount || 0) > 0 && (
                      <>
                        <br />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {money(r.amount, r.currency)} üzerinden {money(r.paid_amount, r.currency)} tahsil edildi
                        </Text>
                      </>
                    )}
                  </>
                ),
              },
            ]}
          />
        </Card>
      )}

      <Modal
        title={itemModal.record ? 'Ücret Kalemini Düzenle' : 'Yeni Ücret Kalemi'}
        open={itemModal.open}
        onOk={submitItem}
        onCancel={() => { setItemModal({ open: false, record: null }); form.resetFields(); }}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="label" label="Kalem Adı" rules={[{ required: true, message: 'Ad girin.' }]}>
            <Input placeholder="Organizasyon Ücreti / Servis / Aracı Kurum" />
          </Form.Item>
          <Space>
            <Form.Item name="amount" label="Tutar" rules={[{ required: true, message: 'Tutar girin.' }]}>
              <InputNumber min={0} style={{ width: 160 }} />
            </Form.Item>
            <Form.Item name="currency" label="Para Birimi">
              <Select style={{ width: 120 }} options={CURRENCIES} />
            </Form.Item>
          </Space>
          <Form.Item name="basis" label="Hesaplama">
            <Select
              options={[
                { value: 'per_person', label: 'Kişi başı (gelen kişi sayısıyla çarpılır)' },
                { value: 'flat', label: 'Sabit (kişi sayısından bağımsız)' },
              ]}
            />
          </Form.Item>
          <Form.Item name="is_optional" valuePropName="checked">
            <Checkbox>İsteğe bağlı (veli onay verirken seçer, örn. servis)</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={planModal.record ? 'Taksit Planı Satırını Düzenle' : 'Yeni Taksit Planı Satırı'}
        open={planModal.open}
        onOk={submitPlanItem}
        onCancel={() => { setPlanModal({ open: false, record: null }); planForm.resetFields(); }}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={planForm} layout="vertical">
          <Form.Item name="label" label="Taksit Adı" rules={[{ required: true, message: 'Ad girin.' }]}>
            <Input placeholder="1. Taksit / Kayıt Ücreti / Final Ödeme" />
          </Form.Item>
          <Space>
            <Form.Item name="currency" label="Para Birimi" rules={[{ required: true, message: 'Seçin.' }]} initialValue="TRY">
              <Select style={{ width: 120 }} options={CURRENCIES} />
            </Form.Item>
            <Form.Item name="no" label="Sıra" extra="Boş bırakılırsa otomatik sıralanır.">
              <InputNumber min={1} max={24} style={{ width: 100 }} />
            </Form.Item>
          </Space>
          <Form.Item
            name="per_person_amount"
            label="Kişi Başı Tutar"
            rules={[{ required: true, message: 'Tutar girin.' }]}
            extra="Katılımcıya uygulanırken o ailenin kişi sayısıyla çarpılır."
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="due_date" label="Vade Tarihi">
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <ParticipantFinanceModal
        open={detail.open}
        participant={detail.participant}
        feeItems={feeItems}
        onClose={() => setDetail({ open: false, participant: null })}
        onChanged={load}
      />
    </div>
  );
};

// ------------------------------------------- Katılımcının ücret/taksit detayı

const ParticipantFinanceModal = ({ open, participant, feeItems, onClose, onChanged }) => {
  const [fees, setFees] = useState(null);
  const [installments, setInstallments] = useState([]);
  // Tahsilati asili kalmis (silinmis) taksitler — normalde BOS.
  const [asili, setAsili] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Tahsilat girisi: tutar elle degistirilebilmeli, cunku belirlenen taksit
  // kadar odeme alinmayan durumlar oluyor (Eren Bey'in bildirdigi sorun).
  const [payModal, setPayModal] = useState({ open: false, record: null, amount: null, saving: false });

  const load = useCallback(async () => {
    if (!participant) return;
    setLoading(true);
    try {
      const [{ data: f }, { data: i }, { data: a }] = await Promise.all([
        getParticipantFees(participant.id),
        getInstallments(participant.id),
        // Neredeyse her zaman bos; dolu donerse para hicbir ekranda
        // gorunmuyor demektir, o yuzden sayfa acilisinda soruluyor.
        getOrphanedInstallments(participant.id),
      ]);
      setFees(f);
      setInstallments(i.data || []);
      setAsili(a.data || []);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Ücret dökümü yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [participant]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const apply = async (selectedIds) => {
    try {
      const { data } = await applyParticipantFees(participant.id, {
        fee_item_ids: selectedIds,
        attendee_count: participant.attendee_count,
      });
      message.success(data.message);
      load();
      onChanged();
    } catch (e) {
      message.error(apiErrorMessage(e));
    }
  };

  // Mevcut plani ezmek ARTIK KAZA DEGIL KARAR.
  //
  // Sunucu, taksiti olan katilimcida 409 + `sebep: 'plan_var'` donuyor ve
  // kac taksitin silinecegini (`existing_count`) soyluyor. Yonetici bunu
  // acikca onaylarsa istek `overwrite: true` ile tekrarlaniyor.
  //
  // ⚠ `sebep: 'tahsilat_var'` BU YOLDAN GECMEZ - tahsilat girilmis bir plan
  // hicbir onayla silinemez, o satirlar tek tek duzenlenir.
  const planUret = async (uzerineYaz = false) => {
    setGenerating(true);
    try {
      const { data } = await generateInstallments(
        participant.id,
        uzerineYaz ? { overwrite: true } : undefined,
      );
      message.success(data.message);
      load();
      onChanged();
    } catch (e) {
      const yanit = e?.response?.data;

      if (yanit?.sebep === 'plan_var') {
        Modal.confirm({
          title: 'Mevcut taksit planı silinecek',
          okText: 'Evet, yeniden üret',
          okButtonProps: { danger: true },
          cancelText: 'Vazgeç',
          width: 520,
          content: (
            <div>
              <p style={{ marginTop: 8 }}>
                Bu katılımcının <b>{yanit.existing_count} taksiti</b> var. Yeniden
                üretmek bunları siler ve etabın güncel planından baştan oluşturur.
              </p>
              <p style={{ marginBottom: 0 }}>
                <Text type="warning">
                  Bu katılımcıya özel yapılmış vade/tutar düzenlemeleri ve elle
                  eklenmiş taksitler de silinir.
                </Text>
              </p>
            </div>
          ),
          onOk: () => planUret(true),
        });
        return;
      }

      message.warning(apiErrorMessage(e));
    } finally {
      setGenerating(false);
    }
  };

  const mandatoryIds = feeItems.filter((i) => !i.is_optional).map((i) => i.id);
  const allIds = feeItems.map((i) => i.id);

  return (
    <Modal
      title={participant ? `${participant.student?.name} ${participant.student?.surname} — Ücret ve Taksitler` : ''}
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
      destroyOnHidden
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Tooltip title="Zorunlu kalemleri bu katılımcıya uygular ve kişi sayısıyla çarpar">
          <Button icon={<ThunderboltOutlined />} onClick={() => apply(mandatoryIds)} disabled={!mandatoryIds.length}>
            Zorunlu Kalemleri Uygula
          </Button>
        </Tooltip>
        <Button icon={<ThunderboltOutlined />} onClick={() => apply(allIds)} disabled={!allIds.length}>
          Tüm Kalemleri Uygula
        </Button>
        <Popconfirm
          title="Etabın taksit planı bu katılımcıya uygulansın mı?"
          description="Vade tarihleri ve kişi başı tutarlar etap ayarlarındaki plandan gelir, sadece kişi sayısıyla çarpılır. Katılımcının zaten bir planı varsa ayrıca onay istenir."
          onConfirm={() => planUret(false)}
          okText="Uygula"
          cancelText="Vazgeç"
        >
          <Button type="primary" icon={<PlusOutlined />} loading={generating}>
            Etap Taksit Planını Uygula
          </Button>
        </Popconfirm>
      </Space>

      <Card size="small" title="Uygulanan Ücretler" loading={loading} style={{ marginBottom: 12 }}>
        <Table
          rowKey="id"
          size="small"
          dataSource={fees?.fees || []}
          pagination={false}
          locale={{ emptyText: 'Henüz ücret uygulanmamış.' }}
          columns={[
            { title: 'Kalem', dataIndex: 'label' },
            { title: 'Birim', dataIndex: 'unit_amount', width: 120, render: (v, r) => money(v, r.currency) },
            { title: 'Adet', dataIndex: 'quantity', width: 70 },
            { title: 'Toplam', dataIndex: 'total_amount', width: 130, render: (v, r) => <b>{money(v, r.currency)}</b> },
          ]}
        />
        {fees?.totals && Object.keys(fees.totals).length > 0 && (
          <Space wrap style={{ marginTop: 12 }}>
            {Object.entries(fees.totals).map(([cur, val]) => (
              <Tag key={cur} color="blue" style={{ fontSize: 13, padding: '4px 10px' }}>
                Borç: {money(val, cur)}
                {fees.balance?.[cur] !== undefined && ` · Kalan: ${money(fees.balance[cur], cur)}`}
              </Tag>
            ))}
          </Space>
        )}
      </Card>

      {/* SALT BİLGİ — tek tıklık düzeltme düğmesi BİLEREK YOK.
          Bir dönem "Geri Al" düğmesi vardı ve canlıda yanlış sonuç verdi:
          plan arada yeniden üretilmişse geri almak hem taksiti hem parayı
          İKİ KEZ sayıyor. Gerçekten olan da buydu (30.10.2026). Ayrımı
          ancak insan yapabilir, o yüzden ekran karar vermiyor; kararı
          verecek bilgiyi veriyor. Ayrıntı: backend/CLAUDE.md. */}
      {asili.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Tahsilatı asılı kalmış taksit"
          description={
            <div>
              <div style={{ marginBottom: 8 }}>
                Aşağıdaki taksit silinmiş ama tahsilatı duruyor; bu para
                öğrenci ekranında, etap finansında ve gün sonu listesinde
                <b> görünmüyor</b>. Düzeltmeden önce aynı paranın başka bir
                taksite yeniden girilmiş olup olmadığını kontrol edin.
              </div>
              {asili.map((t) => (
                <div key={t.id} style={{ marginBottom: 10 }}>
                  <Text strong>{t.label}</Text>{' '}
                  <Text type="secondary">
                    ({money(t.amount, t.currency)} · vade{' '}
                    {t.due_date ? dayjs(t.due_date).format('DD.MM.YYYY') : '—'})
                  </Text>
                  <br />
                  {t.payments.map((o) => (
                    <Text key={o.id} type="secondary" style={{ fontSize: 12 }}>
                      {money(o.amount, t.currency)} ·{' '}
                      {o.paid_at ? dayjs(o.paid_at).format('DD.MM.YYYY HH:mm') : '—'}
                      {o.receiver ? ` · ${o.receiver}` : ''}
                      <br />
                    </Text>
                  ))}
                  {t.live_match ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Aynı dönemin <b>duran bir taksiti var</b>:{' '}
                      {t.live_match.label} — {money(t.live_match.amount, t.currency)},
                      tahsil {money(t.live_match.paid_amount, t.currency)}.
                      Para oraya yeniden girilmişse yukarıdaki satır eski bir
                      denemeden kalmıştır.
                    </Text>
                  ) : (
                    <Text type="danger" style={{ fontSize: 12 }}>
                      Bu dönemin duran bir taksiti YOK — para gerçekten
                      hiçbir ekranda görünmüyor.
                    </Text>
                  )}
                </div>
              ))}
            </div>
          }
        />
      )}

      <Card size="small" title="Taksitler" loading={loading}>
        <Table
          rowKey="id"
          size="small"
          dataSource={installments}
          pagination={false}
          locale={{ emptyText: 'Taksit planı üretilmemiş.' }}
          columns={[
            { title: 'Taksit', dataIndex: 'label', render: (v, r) => v || `${r.no}. Taksit` },
            { title: 'Vade', dataIndex: 'due_date', width: 120, render: (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '—') },
            { title: 'Tutar', dataIndex: 'amount', width: 130, render: (v, r) => money(v, r.currency) },
            {
              title: 'Tahsil Edilen',
              width: 140,
              render: (_, r) => {
                const tahsil = Number(r.paid_amount || 0);
                if (!tahsil) return <Text type="secondary">—</Text>;
                return (
                  <Space orientation="vertical" size={0}>
                    <Text>{money(tahsil, r.currency)}</Text>
                    {r.payment_status === 'partial' && (
                      <Text type="danger" style={{ fontSize: 11 }}>
                        Kalan {money(r.remaining_amount, r.currency)}
                      </Text>
                    )}
                  </Space>
                );
              },
            },
            {
              title: 'Durum',
              width: 160,
              render: (_, r) => {
                if (r.payment_status === 'paid') {
                  return (
                    <Space orientation="vertical" size={0}>
                      <Tag color="success">Ödendi</Tag>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {dayjs(r.paid_at).format('DD.MM.YYYY')}
                        {r.receiver ? ` · ${r.receiver.name} ${r.receiver.surname}` : ''}
                      </Text>
                    </Space>
                  );
                }
                const overdue = r.due_date && dayjs(r.due_date).isBefore(dayjs(), 'day');
                // Kismi odenmis taksit HALA borclu - gecikmisse gecikmis kalir.
                if (r.payment_status === 'partial') {
                  return (
                    <Space orientation="vertical" size={0}>
                      <Tag color="warning">Kısmen Ödendi</Tag>
                      {overdue && <Text type="danger" style={{ fontSize: 11 }}>Vadesi geçti</Text>}
                    </Space>
                  );
                }
                return <Tag color={overdue ? 'error' : 'default'}>{overdue ? 'Gecikti' : 'Bekliyor'}</Tag>;
              },
            },
            {
              title: 'İşlemler',
              width: 150,
              fixed: 'right',
              render: (_, r) => (
                <Space>
                  {r.payment_status !== 'paid' && (
                    <Tooltip title="Tahsilat ekle (kısmi olabilir)">
                      <Button
                        size="small"
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={() => setPayModal({
                          open: true,
                          record: r,
                          // Varsayilan KALAN tutar; eksik tahsilatta elle azaltilir.
                          amount: r.remaining_amount,
                          saving: false,
                        })}
                      />
                    </Tooltip>
                  )}
                  {Number(r.paid_amount || 0) > 0 && (
                    <Tooltip title="Bu taksitin tüm tahsilatlarını geri al">
                      <Button
                        size="small"
                        icon={<UndoOutlined />}
                        onClick={async () => {
                          try {
                            await unpayInstallment(r.id);
                            message.success('Tahsilatlar geri alındı.');
                            load();
                            onChanged();
                          } catch (e) { message.error(apiErrorMessage(e)); }
                        }}
                      />
                    </Tooltip>
                  )}
                  <Popconfirm
                    title="Taksit silinsin mi?"
                    onConfirm={async () => {
                      try {
                        await deleteInstallment(r.id);
                        message.success('Silindi.');
                        load();
                        onChanged();
                      } catch (e) { message.warning(apiErrorMessage(e)); }
                    }}
                    okText="Sil"
                    cancelText="Vazgeç"
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
          expandable={{
            // Bir taksitin birden fazla tahsilati olabilir; yanlis girileni
            // tek tek silebilmek icin gecmis burada aciliyor.
            rowExpandable: (r) => (r.payments || []).length > 0,
            expandedRowRender: (r) => (
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={r.payments || []}
                columns={[
                  // Saat KOŞULSUZ basılıyordu ve geriye dönük girilen her
                  // tahsilat "22.10.2026 00:00" görünüyordu — Eren Bey'in
                  // "tarih-saat tutulmuyor" bildiriminin kaynağı buydu
                  // (22.10.2026). Ortak yardımcı saati yalnızca gerçekten
                  // biliniyorsa yazıyor; aynı kuralı öğrenci detayı ve veli
                  // ekranı da kullanıyor, üçü sapmasın diye tek yerde.
                  { title: 'Tarih', dataIndex: 'paid_at', width: 140, render: (v) => tahsilatZamani(v) },
                  { title: 'Tutar', dataIndex: 'amount', width: 120, render: (v) => money(v, r.currency) },
                  { title: 'Yöntem', dataIndex: 'payment_method', render: (v) => v || '—' },
                  { title: 'Tahsil Eden', render: (_, p) => (p.receiver ? `${p.receiver.name} ${p.receiver.surname}` : '—') },
                  { title: 'Not', dataIndex: 'notes', render: (v) => v || '—' },
                  {
                    title: '',
                    width: 50,
                    render: (_, p) => (
                      <Popconfirm
                        title="Bu tahsilat kaydı silinsin mi?"
                        onConfirm={async () => {
                          try {
                            await deleteInstallmentPayment(p.id);
                            message.success('Tahsilat silindi.');
                            load();
                            onChanged();
                          } catch (e) { message.error(apiErrorMessage(e)); }
                        }}
                        okText="Sil"
                        cancelText="Vazgeç"
                      >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    ),
                  },
                ]}
              />
            ),
          }}
        />
      </Card>

      <Modal
        title="Tahsilat Ekle"
        open={payModal.open}
        onCancel={() => setPayModal({ open: false, record: null, amount: null, saving: false })}
        okText="Kaydet"
        cancelText="Vazgeç"
        confirmLoading={payModal.saving}
        onOk={async () => {
          const r = payModal.record;
          if (!r) return;
          setPayModal((s) => ({ ...s, saving: true }));
          try {
            await payInstallment(r.id, { paid_amount: payModal.amount });
            message.success('Tahsilat kaydedildi.');
            setPayModal({ open: false, record: null, amount: null, saving: false });
            load();
            onChanged();
          } catch (e) {
            message.error(apiErrorMessage(e));
            setPayModal((s) => ({ ...s, saving: false }));
          }
        }}
      >
        {payModal.record && (
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Text type="secondary">
              {payModal.record.label || `${payModal.record.no}. Taksit`} — taksit tutarı{' '}
              {money(payModal.record.amount, payModal.record.currency)}
              {Number(payModal.record.paid_amount || 0) > 0 && (
                <> · şu ana kadar tahsil edilen {money(payModal.record.paid_amount, payModal.record.currency)}</>
              )}
            </Text>
            <div>
              <Text strong>Tahsil edilen tutar</Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={0.01}
                max={payModal.record.remaining_amount}
                step={50}
                value={payModal.amount}
                onChange={(v) => setPayModal((s) => ({ ...s, amount: v }))}
                addonAfter={payModal.record.currency}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Kalan borç {money(payModal.record.remaining_amount, payModal.record.currency)}. Daha
                azını girerseniz taksit “kısmen ödendi” kalır ve kalan tutar borç olarak görünmeye
                devam eder.
              </Text>
            </div>
          </Space>
        )}
      </Modal>
    </Modal>
  );
};

export default StageFinanceTab;
